import { bind } from 'app/utils/decorators/decoratorUtils';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { Vector as VectorSource } from 'ol/source';
import { calculateSubLayerZIndex, Z_INDEX_INCREMENT, applyLayerZIndex } from 'app/config/layerZIndexConfig';
import { fromLonLat } from 'ol/proj';
import { isDefined } from 'app/utils/utils';

import AffectliVectorLayer from './AffectliVectorLayer';

import { loadEntitiesRelationsPins } from 'store/actions/maps/situationalAwarenessActions';
import { entityPinStyleSimple } from 'app/components/molecules/Map/EntityPin/entityPinStyle';
import { fetchMapRelations } from 'store/actions/entities/relationshipsActions';

class RelatedEntityLayer extends AffectliVectorLayer {
    parentLayer;
    relatedEntities;
    dataSource;
    layer;
    lineFeatures = [];

    constructor(layer, relatedEntities, info, olMap) {
        super();
        this.parentLayer = layer;
        this.info = info;
        this.layerId = info.id;
        this.olMap = olMap;
        this.relatedEntities = relatedEntities;

        const parentZIndex = layer?.zIndex || Z_INDEX_INCREMENT;
        this.zIndex = info.zIndex || calculateSubLayerZIndex(parentZIndex, 'related_sub_entity');
        
        this.createRelatedLayer();
        this.parentLayer.addRelatedEntities(this);
    }

    @bind
    createRelatedLayer() {
        this.layer = new VectorLayer({
            title: 'related entities layer',
            visible: true,
            attributes: {
                id: this.layerId,
                type: 'related',
                subtitle: 'entities',
                layerType: 'entity-layer',
                iconName: 'things',
                iconType: 'af',
                noLocationPins: [],
            },
            source: this.getRelatedEntitySource(),
            style: this.styleFunction,
            zIndex: this.zIndex,
        });
        const isLayer = this.olMap.findLayer('allLayers', 'related entities layer') || this.olMap.findLayerByType(this.info.id, 'related');
        if (isLayer) {
            this.olMap?.getMap()?.removeLayer(isLayer);
        }
        this.olMap.getMap()?.addLayer(this.layer);
    }

    @bind
    styleFunction(feature, resolution) {
        return entityPinStyleSimple(feature, this.olMap.getMap(), this.zIndex);
    }

    @bind
    getLayer() {
        return this.layer;
    }

    @bind
    async loader(extent, resolution, projection) {
        try {
            if (this.parentLayer && typeof this.parentLayer.getFeatures === 'function') {
                await this.parentLayer.getFeatures();
            }
            this.lineFeatures.length = 0;
            this.features.length = 0;
            await this.getRelatedFeatures();
            
            this.dataSource.addFeatures(this.features);
            this.dataSource.addFeatures(this.lineFeatures);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[RELATED] loader error:', e);
        }
    }

    @bind
    getRelatedEntitySource() {
        if (!this.dataSource) {
            this.dataSource = new VectorSource({
                loader: this.loader,
            });
        }
        return this.dataSource;
    }

    @bind
    async getRelatedFeatures() {
        // Check for related_entities_th in multiple possible locations
        let relatedTypeAheadId;
        let related_entities;
        
        // First check the direct info object for related_entities_th
        if (this.info?.related_entities_th?.id) {
            relatedTypeAheadId = this.info.related_entities_th.id;
            related_entities = {
                related_entities_th: this.info.related_entities_th,
                subType: this.info.subType,
                relatedEntities: this.info.relatedEntities
            };
        }
        // Then check under related_entities.related_entities_th 
        else if (this.info?.related_entities?.related_entities_th?.id) {
            relatedTypeAheadId = this.info.related_entities.related_entities_th.id;
            related_entities = this.info.related_entities;
        }
        // Finally check under primary.related_entities (legacy structure)
        else if (this.info?.primary?.related_entities?.related_entities_th?.id) {
            const primaryRelatedEntities = this.info.primary.related_entities;
            relatedTypeAheadId = primaryRelatedEntities.related_entities_th.id;
            related_entities = primaryRelatedEntities;
        }
        
        if (!relatedTypeAheadId) {
            return;
        }
        const entitiesRelations = await fetchMapRelations({
            entityType: this.info.entity_type?.uri || this.info.primary?.entity_type?.uri,
            filterBy: {
                field: 'relation.relationDefinition.id',
                op: '=',
                value: relatedTypeAheadId,
            },
        });

        const relations = entitiesRelations?.data?.relations;
        const relationArray = [];
        for (const relation of relations) {
            relationArray.push(relation?.relatedEntity?.id);
        }
        const { related_entities_th, subType, relatedEntities } = related_entities || {};
        const { relatedType } = related_entities_th || {};
        if (relationArray.length) {
            const variables = [
                { field: 'id', op: 'in', value: relationArray },
                { field: 'geom', op: 'is not null' },
            ];
            const relatedLayerType = relatedType;
            const entityRelationLocation = relatedType && (await loadEntitiesRelationsPins({ type: relatedLayerType, filterBy: variables }));
            entityRelationLocation && relationArray.push(entityRelationLocation);
            let entities = entityRelationLocation?.data?.entities;
            if (subType) entities = entities?.filter(entity => entity.type === subType);
            entities.length > 0 &&
                entities.forEach(relatedEntity => {
                    const filterRelations = relations.filter(relation => relation.relatedEntity.id === relatedEntity.id);
                    let feature = null;
                    filterRelations.forEach(async filter => {
                        const parentEntity = filter.entity;
                        const lineAttributes = filter?.relation?.attributes || {};
                        const parentEntityFeature = this.parentLayer.getFeatureById(parentEntity.id);
                        const locationInfo = relatedEntity && relatedEntity.primary.locationInfo;
                        const { name } = relatedEntity || '';
                        const { related_entities } = this.info;
                        const { iconName, valLineStyle, valLineColour, valLineWidth, valFillColour, valPointSize } = related_entities || {};
                        const { latitude, longitude } = locationInfo || {};
                        const relatedEntityGeometry = new Point(fromLonLat([longitude, latitude]));
                        feature = new Feature({
                            geometry: relatedEntityGeometry,
                            properties: {
                                id: relatedEntity.id,
                            },
                        });
                        const parentCoords = parentEntityFeature && parentEntityFeature.getGeometry().getCoordinates();
                        const relatedCoords = relatedEntityGeometry.getCoordinates();
                        const relatedAttributes = related_entities || {};

                        if (parentCoords && relatedCoords) {
                            const nameArr = ['valPointSize', 'valFillColour'];

                            const data = nameArr.reduce((data, key) => {
                                const attName = relatedAttributes[key];
                                data[key] = relatedEntity?.primary[attName];
                                return data;
                            }, {});
                            const coordinates = [parentCoords, relatedCoords];
                            const connectedLine = new LineString(coordinates);
                            const lineFeature = new Feature({
                                geometry: connectedLine,
                                properties: {
                                    parentId: parentEntity.id,
                                    relatedId: relatedEntity.id,
                                    lineWidth: lineAttributes[valLineWidth] || valLineWidth || 1,
                                    lineColor: lineAttributes[valLineColour] || valLineColour || 'blue',
                                    lineDashed: valLineStyle !== 'solid',
                                },
                            });
                            const rgbColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
                            const isValidRgbColor = rgbColorRegex.test(valFillColour);
                            const pointSize = isDefined(data['valPointSize']) ? data['valPointSize'] : valPointSize ? valPointSize : 32;
                            const pointColor = isDefined(data['valFillColour']) ? data['valFillColour'] : isValidRgbColor ? valFillColour : 'blue';
                            feature.set('attributes', {
                                name: name,
                                iconName: iconName,
                                iconColor: pointColor || 'blue',
                                size: pointSize || 32,
                                entityType: relatedType,
                                id: relatedEntity?.id,
                                related: true,
                                entityType: relatedEntity?.type,
                            });
                            if (relatedEntities && lineFeature && lineFeature.getGeometry() && feature && feature.getGeometry()) {
                                this.lineFeatures.push(lineFeature);
                                this.addFeature(feature);
                            }
                        }
                    });
                });
        }
        this.olMap.getMap().render();
    }

    @bind
    clearRelatedSource(info) {
        this.updateInfo(info);
        this.isFeatureLoaded = false;
        this.lineFeatures.length = 0;
        this.features.length = 0;
        this.layer.getSource().clear();
    }

    @bind
    updateInfo(newPrimary) {
        const { primary, type, ...rest } = this.info;
        this.info = {
            ...newPrimary,
            type: type,
            ...rest,
        };
        this.setFeatureStatus(false);
    }

    @bind
    updateZIndex() {
        const parentZIndex = this.parentOriginalZIndex || this.parentLayer?.zIndex;
        const relatedZIndex = calculateSubLayerZIndex(parentZIndex, 'related_sub_entity');
        
        this.zIndex = relatedZIndex;
        applyLayerZIndex(this.layer, relatedZIndex);
        return relatedZIndex;
    }

    /**
     * Stores the parent layer's original z-index for preserving correct hierarchy
     * when layers are updated and sub-layers are recreated
     */
    storeParentOriginalZIndex(originalZIndex) {
        if (typeof originalZIndex === 'number') {
            this.parentOriginalZIndex = originalZIndex;
            this.updateZIndex();
        }
    }
}

export default RelatedEntityLayer;
